// "Install game": Chrome, Edge and Android hand the page a deferred install prompt. iPhone Safari has none,
// so there the button explains Share → Add to Home Screen instead.
interface InstallPrompt extends Event {
  prompt(): Promise<unknown>;
}

let deferred: InstallPrompt | null = null;
// Read once at startup: display-mode can also match later when the page itself goes fullscreen.
let installed =
  matchMedia('(display-mode: fullscreen), (display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const isIos = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export function canInstall(): boolean {
  return !installed && (deferred !== null || isIos());
}

export function watchInstall(onChange: () => void): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault(); // keep it for our button instead of the browser's mini-infobar
    deferred = event as InstallPrompt;
    onChange();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installed = true;
    onChange();
  });
}

// Shows the browser's install prompt; false when there is none (iPhone), so the caller explains instead.
export async function promptInstall(): Promise<boolean> {
  const event = deferred;
  if (!event) return false;
  deferred = null; // each prompt event can be shown only once
  await event.prompt().catch(() => undefined);
  return true;
}
